import React, { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import paths from 'assets/routes/paths';
import { saveReturnUrl } from 'utils/returnUrl';
import useAuth from '../../hooks/useAuth';
import { ConfigProvider, Drawer, Layout, Skeleton, Spin, BackTop, Button } from 'antd';
import { Content, Header } from 'antd/es/layout/layout';
import Sider from 'antd/es/layout/Sider';
import MenuSider from './elements/MenuSider';

import HeaderSide from './elements/HeaderSide';
import Footer from 'components/global/Footer';
import Profile from 'components/global/Profile';
import { ErrorCaptureProvider } from 'providers/ErrorCapture/ErrorCaptureProvider';
import ReportIncidentFloatButton from 'components/support/ReportIncidentFloatButton';
import { BreadcrumbProvider, Breadcrumbs } from 'components/breadcrumbs';
import { AyudaProvider, PanelNovedades } from 'components/ayuda';

const MOSTRAR_PANEL_EMERGENTE = false;

const defaultDrawer = {
  open: false,
  children: null,
};

export const isAllowedUserType = (type) =>
  ['administrador', 'especialista', 'ejecutivo', 'supervisor', 'liftlogic'].includes(type);

function LayoutMain(props) {
  const { children } = props;

  const { isLoading, user, perfilActivo } = useAuth();

  const [collapsed, setCollapsed] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [drawer, setDrawer] = useState(defaultDrawer);

  const siderRef = useRef(null);

  const handleClickOutside = (event) => {
    // Solo ocultar el sidebar en mobile cuando se hace clic fuera
    if (isMobile && siderRef.current && !siderRef.current.contains(event.target)) {
      setCollapsed(true);
    }
  };

  const handleResize = () => {
    const mobile = window.innerWidth < 768;
    setIsMobile(mobile);

    // En desktop, restaurar el estado anterior del sidebar
    if (!mobile) {
      setCollapsed(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleResize);
    };
  }, [isMobile]);

  const openProfile = () => {
    setDrawer({
      open: true,
      children: <Profile user={user} onClose={closeDrawer} />,
    });
  };

  const closeDrawer = () => {
    setDrawer(defaultDrawer);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin size="large" />
      </div>
    );
  }

  if (user && isAllowedUserType(user.type)) {
    return (
      <ConfigProvider>
        <ErrorCaptureProvider>
          <AyudaProvider>
            <Layout className="min-h-screen bg-gray-50">
              {/* Profile Drawer */}
              <Drawer
                placement="right"
                closable={false}
                onClose={closeDrawer}
                open={drawer.open}
                width={window.innerWidth < 768 ? '100%' : 600}
                className="profile-drawer"
                title={
                  <div className="flex items-center justify-between w-full">
                    <span className="text-lg font-semibold text-gray-800">Mi Perfil</span>
                    <Button
                      type="text"
                      icon={<span className="text-xl">×</span>}
                      onClick={closeDrawer}
                      className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100"
                    />
                  </div>
                }
                styles={{
                  body: {
                    padding: 0,
                    overflow: 'auto',
                  },
                }}
                maskClosable={true}
              >
                {drawer.children}
              </Drawer>

              {/* Main Header */}
              <Header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm border-b border-gray-200 h-16 px-4 md:px-6 flex items-center">
                <HeaderSide
                  collapsed={collapsed}
                  setCollapsed={setCollapsed}
                  openProfile={openProfile}
                />
              </Header>

              {/* Mobile Overlay */}
              {!collapsed && isMobile && (
                <div
                  className="fixed inset-0 bg-black bg-opacity-50 z-30"
                  onClick={() => setCollapsed(true)}
                />
              )}

              {/* Sidebar */}
              <Sider
                ref={siderRef}
                collapsible
                collapsed={collapsed}
                onCollapse={setCollapsed}
                className={`fixed left-0 top-16 bottom-0 z-40 transition-all duration-300 ease-in-out shadow-lg ${
                  isMobile ? (collapsed ? '-translate-x-full' : 'translate-x-0') : ''
                }`}
                width={288}
                collapsedWidth={80}
                theme="light"
              >
                <div className="h-full overflow-y-auto">
                  <MenuSider type={perfilActivo ?? user.type} />
                </div>
              </Sider>

              {/* Main Content Area */}
              <Layout
                className={`transition-all duration-300 ease-in-out mt-16 ${
                  isMobile ? 'ml-0' : collapsed ? 'ml-20' : 'ml-72'
                }`}
              >
                <Content className="min-h-screen">
                  <BreadcrumbProvider>
                    <div className="p-4 md:p-6">
                      <Breadcrumbs />
                      {children}
                    </div>
                  </BreadcrumbProvider>
                </Content>
                <Footer />
              </Layout>
              <ReportIncidentFloatButton />
              {/* Panel emergente desactivado: "visto" vive en localStorage y se repite en cada navegador.
                  Reactivar al guardar lo leído por usuario (ver pendientes.md). */}
              {MOSTRAR_PANEL_EMERGENTE ? <PanelNovedades /> : null}
            </Layout>
          </AyudaProvider>
        </ErrorCaptureProvider>
      </ConfigProvider>
    );
  } else {
    // Preservar el destino (deep-link) para restaurarlo tras el login.
    saveReturnUrl(window.location.hash);
    return <Navigate to={paths.login} replace />;
  }
}

export default LayoutMain;
